import { Controller } from "@hotwired/stimulus";

// Fiscalités Résidence principale : `ptz` (primo-accédant) et
// `no_dispositive_residence`. Doit rester aligné sur
// `Listing::MAIN_RESIDENCE_TAX_PROGRAMS`.
const RESIDENCE_TAX_PROGRAMS = ["ptz", "no_dispositive_residence"];

// fiscality-mode (Stimulus controller) — cf. issues #308 / #381
//
// Pilote le sélecteur de fiscalité unifié de la barre « Profil client ».
//
// Architecture :
// - Un SEUL groupe de radios `criterias[tax_program]` (toutes les fiscalités,
//   PTZ et no_dispositive_residence inclus) constitue la source de vérité. Un
//   radio group ne sérialise qu'UNE valeur : le risque de duplication de #272
//   disparaît structurellement. Les radios sont visuellement masqués (sr-only)
//   mais toujours actifs (jamais `disabled`) pour rester sérialisables et piloter
//   le toggle `tax_program`.
// - En mode Investissement, un « bouton fiscalité » (btn-group-toggle de labels)
//   sélectionne le radio invest correspondant.
// - En mode Résidence principale, un interrupteur « Primo-accédant » bascule
//   entre `ptz` (ON) et `no_dispositive_residence` (OFF) en cochant le radio
//   correspondant (cf. #381).
// - La bascule RP/Investissement (`profile[investment_mode]`, non persistée)
//   positionne le radio tax_program puis déclenche `toggle#changed`,
//   `api-sync#clearFiscalityPatch`, l'event `input` (api-sync + persistance).
export default class extends Controller {
  static targets = ["radio", "fiscalityButton", "primoSwitch"];

  connect() {
    // Mémorise la dernière fiscalité choisie dans chaque mode, pour la restaurer
    // quand on bascule d'un mode à l'autre.
    this.lastInvest = this.checkedInvestValue() || this.firstInvestValue();
    this.lastResidence = this.checkedResidenceValue() || "ptz";
    this.syncActiveButtons();
    this.syncPrimoSwitch();
  }

  // Bascule RP/Investissement (clic sur le label, data-mode).
  modeChanged(event) {
    const mode = event.currentTarget.dataset.mode;
    const isResidence = mode === "residence";
    const value = isResidence
      ? (this.lastResidence || "ptz")
      : (this.lastInvest || this.firstInvestValue());
    this.selectRadio(value);
  }

  // Clic sur un bouton de fiscalité (mode Investissement).
  pick(event) {
    const value = event.currentTarget.dataset.fiscalityValue;
    if (!value) return;
    this.selectRadio(value);
  }

  // Interrupteur « Primo-accédant » (mode Résidence principale) :
  // ON ⇒ `ptz`, OFF ⇒ `no_dispositive_residence`.
  togglePrimo() {
    const value =
      this.hasPrimoSwitchTarget && this.primoSwitchTarget.checked
        ? "ptz"
        : "no_dispositive_residence";
    this.selectRadio(value);
  }

  // Un radio tax_program a changé (programmatiquement ou non) : mémorise la
  // fiscalité et resynchronise l'état actif des boutons et de l'interrupteur.
  radioChanged(event) {
    this.memorize(event.target.value);
    this.syncActiveButtons();
    this.syncPrimoSwitch();
  }

  selectRadio(value) {
    const radio = this.radioTargets.find((r) => r.value === value);
    if (!radio) return;
    if (radio.checked) {
      // Déjà sélectionné : on resynchronise quand même l'affichage.
      this.syncActiveButtons();
      this.syncPrimoSwitch();
      return;
    }
    radio.checked = true;
    this.memorize(value);
    // change → toggle#changed + api-sync#clearFiscalityPatch + radioChanged + api-sync
    radio.dispatchEvent(new Event("change", { bubbles: true }));
    // input → persistance edit-profile + api-sync (les selects/radios n'émettent
    // pas d'event input nativement au clic).
    radio.dispatchEvent(new Event("input", { bubbles: true }));
    this.syncActiveButtons();
    this.syncPrimoSwitch();
  }

  // Mémorise la dernière fiscalité retenue dans son mode (invest ou résidence)
  // pour la restaurer lors d'une future bascule de mode.
  memorize(value) {
    if (!value) return;
    if (this.isResidence(value)) {
      this.lastResidence = value;
    } else {
      this.lastInvest = value;
    }
  }

  syncActiveButtons() {
    const current = this.checkedValue();
    this.fiscalityButtonTargets.forEach((btn) => {
      const active = btn.dataset.fiscalityValue === current;
      btn.classList.toggle("active", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  // Reflète l'état courant du radio sur l'interrupteur « Primo-accédant »
  // (`ptz` ⇒ ON, `no_dispositive_residence` ⇒ OFF). Ne touche pas l'interrupteur
  // quand la fiscalité courante est un dispositif d'investissement.
  syncPrimoSwitch() {
    if (!this.hasPrimoSwitchTarget) return;
    const current = this.checkedValue();
    if (this.isResidence(current)) {
      this.primoSwitchTarget.checked = current === "ptz";
    }
  }

  isResidence(value) {
    return RESIDENCE_TAX_PROGRAMS.includes(value);
  }

  checkedValue() {
    const checked = this.radioTargets.find((r) => r.checked);
    return checked ? checked.value : null;
  }

  checkedInvestValue() {
    const value = this.checkedValue();
    return value && !this.isResidence(value) ? value : null;
  }

  checkedResidenceValue() {
    const value = this.checkedValue();
    return value && this.isResidence(value) ? value : null;
  }

  firstInvestValue() {
    const first = this.radioTargets.find((r) => !this.isResidence(r.value));
    return first ? first.value : null;
  }
}
